import { useState, useEffect, useContext } from 'react'

import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'
import { toast } from 'react-toastify'
import useAuth from '../../hooks/useAuth.js'
import formatCpf from '../../utils/formatCpf'
import validateCPF from '../../utils/validateCPF'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  FormHelperText,
  // TODO non used
  // Switch,
  // TODO non used
  // FormControlLabel,
} from '@material-ui/core'

import { Visibility, VisibilityOff } from '@material-ui/icons'

import { makeStyles } from '@material-ui/core/styles'
import { green } from '@material-ui/core/colors'
import { i18n } from '../../translate/i18n'

import api from '../../services/api'
import toastError from '../../errors/toastError'
import QueueSelect from '../QueueSelect'
import { AuthContext } from '../../context/Auth/AuthContext'
import { Can } from '../Can'
import useWhatsApps from '../../hooks/useWhatsApps'
import OnlyForSuperUser from '../../components/OnlyForSuperUser'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  multFieldLine: {
    display: 'flex',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: 'relative',
  },

  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
}))

const UserSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Digite ao menos 2 caracteres').max(50, 'Limite de 50 caracteres').required('Obrigatório'),
  password: Yup.string().min(5, 'Digite ao menos 5 caracteres').max(50, 'Limite de 50 caracteres'),
  email: Yup.string().email('Email inválido').required('Obrigatório'),
  cpf: Yup.string()
    .transform(value => (value === null ? '' : value))
    .nullable()
    .test('valid-cpf', function (value) {
      if (!value) return true
      const digits = value.replace(/\D/g, '')
      if (digits.length < 11 || !validateCPF(digits)) {
        return this.createError({ message: 'Não é um CPF válido' })
      }
      return true
    }),
})

const UserModal = ({ open, onClose, userId, disableEmail = false }) => {
  const classes = useStyles()

  const [currentUser, setCurrentUser] = useState({})
  const { getCurrentUserInfo } = useAuth()

  const initialState = {
    name: '',
    email: '',
    cpf: '',
    password: '',
    profile: 'user',
    roleId: '',
    super: '',
    SuperIs: '0',
    allTicket: 'desabled',
    viewAllTickets: 'desabled',
  }

  const { user: loggedInUser } = useContext(AuthContext)

  const [user, setUser] = useState(initialState)
  const [selectedQueueIds, setSelectedQueueIds] = useState([])
  const [whatsappId, setWhatsappId] = useState(false)
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([])
  const [showPassword, setShowPassword] = useState(false)
  const [roles, setRoles] = useState([])
  const { loading, whatsApps } = useWhatsApps()

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        setUser(initialState)
        setSelectedWhatsappIds([])
        return
      }
      try {
        const { data } = await api.get(`/users/${userId}`)
        const superValue = data.super !== undefined && data.super !== null ? String(data.super) : '0'
        setUser(prevState => {
          return { ...prevState, ...data, cpf: data.cpf || '', roleId: data.roleId || data.role?.id || '', SuperIs: superValue }
        })
        const userQueueIds = data.queues?.map(queue => queue.id)
        setSelectedQueueIds(userQueueIds)
        setWhatsappId(data.whatsappId ? data.whatsappId : '')
        setSelectedWhatsappIds(data.whatsapps?.map(whatsapp => whatsapp.id) || [])

        const userS = await getCurrentUserInfo()
        setCurrentUser(userS)
      } catch (err) {
        toastError(err)
      }
    }

    fetchUser()
    // TODO non used
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, open])

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { data } = await api.get('/roles')
        setRoles(data)
      } catch (err) {
        toastError(err)
      }
    }

    if (open) {
      fetchRoles()
    }
  }, [open])

  const handleClose = () => {
    onClose()
    setUser(initialState)
  }

  const defaultWhatsappOptions = selectedWhatsappIds.length > 0 ? whatsApps.filter(whatsapp => selectedWhatsappIds.includes(whatsapp.id)) : whatsApps

  const handleSelectedWhatsappIds = values => {
    setSelectedWhatsappIds(values)
    if (values.length > 0 && whatsappId && !values.includes(whatsappId)) {
      setWhatsappId('')
    }
  }

  const handleSaveUser = async values => {
    const userData = {
      ...values,
      roleId: Number(values.roleId),
      whatsappId,
      whatsappIds: selectedWhatsappIds,
      queueIds: selectedQueueIds,
      allTicket: values.allTicket,
      viewAllTickets: values.viewAllTickets,
      super: values.SuperIs ? parseInt(values.SuperIs, 10) : 0,
    }
    try {
      if (userId) {
        await api.put(`/users/${userId}`, userData)
      } else {
        await api.post('/users', userData)
      }
      toast.success(i18n.t('userModal.success'))
    } catch (err) {
      toastError(err)
    }
    handleClose()
  }

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth scroll='paper'>
        <DialogTitle id='form-dialog-title'>{userId ? `${i18n.t('userModal.title.edit')}` : `${i18n.t('userModal.title.add')}`}</DialogTitle>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values)
              actions.setSubmitting(false)
            }, 400)
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t('userModal.form.name')}
                    autoFocus
                    name='name'
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant='outlined'
                    margin='dense'
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    name='password'
                    variant='outlined'
                    margin='dense'
                    fullWidth
                    label={i18n.t('userModal.form.password')}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton aria-label='toggle password visibility' onClick={() => setShowPassword(e => !e)}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field name='email'>
                    {({ field, form }) => (
                      <TextField
                        {...field}
                        label={i18n.t('userModal.form.email')}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        variant='outlined'
                        margin='dense'
                        fullWidth
                        onChange={e => form.setFieldValue('email', e.target.value.toLowerCase())}
                        disabled={disableEmail || userId === loggedInUser.id}
                      />
                    )}
                  </Field>
                  <FormControl variant='outlined' className={classes.formControl} margin='dense'>
                    <Can
                      role={loggedInUser.profile}
                      perform='user-modal:editProfile'
                      yes={() => (
                        <>
                          <InputLabel id='role-selection-input-label'>{i18n.t('userModal.form.profile')}</InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t('userModal.form.profile')}
                            name='roleId'
                            labelId='role-selection-input-label'
                            id='role-selection'
                            required
                            disabled={userId === loggedInUser.id && loggedInUser.profile === 'admin'}
                          >
                            {roles.map(role => (
                              <MenuItem key={role.id} value={role.id}>
                                {role.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </>
                      )}
                    />
                  </FormControl>
                </div>
                <div className={classes.multFieldLine}>
                  <Field name='cpf'>
                    {({ field, form }) => {
                      const handleChange = e => {
                        const { formatted } = formatCpf(e.target.value)
                        form.setFieldValue('cpf', formatted)
                      }
                      return (
                        <TextField
                          {...field}
                          label={i18n.t('userModal.form.cpf')}
                          variant='outlined'
                          margin='dense'
                          fullWidth
                          value={field.value}
                          onChange={handleChange}
                          onBlur={field.onBlur}
                          error={touched.cpf && Boolean(errors.cpf)}
                          helperText={touched.cpf && errors.cpf}
                          placeholder='000.000.000-00'
                        />
                      )
                    }}
                  </Field>
                </div>
                {userId && (
                  <FormControl variant='outlined' margin='dense' className={classes.maxWidth} fullWidth>
                    <OnlyForSuperUser
                      user={currentUser}
                      yes={() => (
                        <>
                          <InputLabel id='SuperIs-selection-input-label'>{i18n.t('userModal.form.SuperIs')}</InputLabel>

                          <Field as={Select} label={i18n.t('userModal.form.SuperIs')} name='SuperIs' labelId='SuperIs-selection-label' id='SuperIs'>
                            <MenuItem value='0'>NÃO</MenuItem>
                            <MenuItem value='1'>SIM</MenuItem>
                          </Field>
                        </>
                      )}
                    />
                  </FormControl>
                )}
                <Can role={loggedInUser.profile} perform='user-modal:editQueues' yes={() => <QueueSelect selectedQueueIds={selectedQueueIds} onChange={values => setSelectedQueueIds(values)} />} />
                <Can
                  role={loggedInUser.profile}
                  perform='user-modal:editProfile'
                  yes={() => (
                    <>
                      <FormControl variant='outlined' margin='dense' fullWidth>
                        <InputLabel id='whatsapps-selection-input-label'>{i18n.t('userModal.form.whatsapps')}</InputLabel>
                        <Select
                          multiple
                          labelId='whatsapps-selection-input-label'
                          label={i18n.t('userModal.form.whatsapps')}
                          value={selectedWhatsappIds}
                          onChange={e => handleSelectedWhatsappIds(e.target.value)}
                          renderValue={selected => (
                            <div className={classes.chips}>
                              {selected.map(id => {
                                const whatsapp = whatsApps.find(item => item.id === id)
                                return whatsapp ? <Chip key={id} variant='outlined' label={<span translate='no'>{whatsapp.name}</span>} className={classes.chip} /> : null
                              })}
                            </div>
                          )}
                        >
                          {whatsApps.map(whatsapp => (
                            <MenuItem key={whatsapp.id} value={whatsapp.id} translate='no' className='notranslate'>
                              {whatsapp.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{i18n.t('userModal.form.whatsappsHelper')}</FormHelperText>
                      </FormControl>
                      <FormControl variant='outlined' margin='dense' className={classes.maxWidth} fullWidth>
                        <InputLabel>{i18n.t('userModal.form.whatsapp')}</InputLabel>
                        <Field as={Select} value={whatsappId} onChange={e => setWhatsappId(e.target.value)} label={i18n.t('userModal.form.whatsapp')}>
                          <MenuItem value={''}>&nbsp;</MenuItem>
                          {defaultWhatsappOptions.map(whatsapp => (
                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                              {whatsapp.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </>
                  )}
                />
                <Can
                  role={loggedInUser.profile}
                  perform='user-modal:editProfile'
                  yes={() =>
                    !loading && (
                      <div className={classes.textField}>
                        <FormControl variant='outlined' className={classes.maxWidth} margin='dense' fullWidth>
                          <>
                            <InputLabel id='profile-selection-input-label'>{i18n.t('userModal.form.allTicket')}</InputLabel>

                            <Field as={Select} label={i18n.t('allTicket.form.viewTags')} name='allTicket' labelId='allTicket-selection-label' id='allTicket-selection' required>
                              <MenuItem value='enabled'>{i18n.t('userModal.form.allTicketEnabled')}</MenuItem>
                              <MenuItem value='desabled'>{i18n.t('userModal.form.allTicketDesabled')}</MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </div>
                    )
                  }
                />
                <Can
                  role={loggedInUser.profile}
                  perform='user-modal:editProfile'
                  yes={() =>
                    !loading && (
                      <div className={classes.textField}>
                        <FormControl variant='outlined' className={classes.maxWidth} margin='dense' fullWidth>
                          <>
                            <InputLabel id='viewAllTickets-selection-input-label'>{i18n.t('userModal.form.viewAllTickets')}</InputLabel>

                            <Field as={Select} label={i18n.t('userModal.form.viewAllTickets')} name='viewAllTickets' labelId='viewAllTickets-selection-label' id='viewAllTickets-selection' required>
                              <MenuItem value='enabled'>{i18n.t('userModal.form.viewAllTicketsEnabled')}</MenuItem>
                              <MenuItem value='desabled'>{i18n.t('userModal.form.viewAllTicketsDesabled')}</MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </div>
                    )
                  }
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color='secondary' disabled={isSubmitting} variant='outlined'>
                  {i18n.t('userModal.buttons.cancel')}
                </Button>
                <Button type='submit' color='primary' disabled={isSubmitting} variant='contained' className={classes.btnWrapper}>
                  {userId ? `${i18n.t('userModal.buttons.okEdit')}` : `${i18n.t('userModal.buttons.okAdd')}`}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  )
}

export default UserModal
